import React, { useState, useEffect, useRef, useMemo } from 'react';
import GeneLogo from '../Logo/GeneLogo';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm'; // ✅ FIXED: Added missing import
import './ChatInterface.css';
import DownloadButtons from '../Download/DownloadButtons';
import { useAuth } from '../../contexts/AuthContext';
import MapIntegration from './MapIntegration'; // ✅ NEW: Import for map integration

const ChatInterface = (props) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [currentConversation, setCurrentConversation] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [requestType, setRequestType] = useState('regular');
  const [error, setError] = useState(null);
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [sentDocuments, setSentDocuments] = useState([]); // docs already in conversation history
  const [showDocumentSelector, setShowDocumentSelector] = useState(false);
  const [showDocumentPopup, setShowDocumentPopup] = useState(false);
  const [editingConversationId, setEditingConversationId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [viewingImage, setViewingImage] = useState(null);
  const [referencedImages, setReferencedImages] = useState({});
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isConversationAtCapacity, setIsConversationAtCapacity] = useState(false);
  const [showMap, setShowMap] = useState(false); // ✅ NEW: State for map visibility
  
  // Search functionality state
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  
  const messagesEndRef = useRef(null);

  // ✅ CARMACK ENHANCED: Smart table detection for proper CSS class application
  const markdownComponents = useMemo(() => ({
    table: ({node, children, ...props}) => {
      // Extract text content from table to detect type
      let tableContent = '';
      
      // Safe extraction of table text content
      try {
        if (node && node.children) {
          tableContent = node.children.map(section => 
            section.children?.map(row => 
              row.children?.map(cell => 
                cell.children?.map(content => 
                  content.value || ''
                ).join(' ')
              ).join(' ')
            ).join(' ')
          ).join(' ').toLowerCase();
        }
      } catch (e) {
        console.log('Table content extraction failed, using default styling');
      }
      
      // Detect decade summary tables
      const isDecadeSummary = tableContent.includes('1870s') || 
                             tableContent.includes('1880s') || 
                             tableContent.includes('1890s') ||
                             tableContent.includes('1900s') ||
                             tableContent.includes('1910s') ||
                             tableContent.includes('1920s') ||
                             tableContent.includes('1930s') ||
                             tableContent.includes('1940s') ||
                             tableContent.includes('1950s') ||
                             tableContent.includes('generation') ||
                             tableContent.includes('decade') ||
                             tableContent.includes('immigration wave') ||
                             tableContent.includes('family building') ||
                             tableContent.includes('establishment');
      
      // Apply appropriate CSS classes
      const tableClasses = isDecadeSummary ? 'gene-table decade-summary' : 'gene-table';
      
      return <table className={tableClasses} {...props}>{children}</table>;
    },
    thead: ({node, ...props}) => (
      <thead className="gene-thead" {...props} />
    ),
    tbody: ({node, ...props}) => (
      <tbody className="gene-tbody" {...props} />
    ),
    tr: ({node, ...props}) => (
      <tr className="gene-tr" {...props} />
    ),
    th: ({node, ...props}) => (
      <th className="gene-th" {...props} />
    ),
    td: ({node, ...props}) => (
      <td className="gene-td" {...props} />
    ),
    code: ({node, inline, className, children, ...props}) => {
      if (inline) {
        return <code className="gene-inline-code" {...props}>{children}</code>
      }
      return <pre className="gene-code-block"><code className={className} {...props}>{children}</code></pre>
    },
    blockquote: ({node, ...props}) => (
      <blockquote className="gene-blockquote" {...props} />
    )
  }), []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToHideFooter = () => {
    const chatHeader = document.querySelector('.chat-header');
    if (chatHeader) {
      const headerRect = chatHeader.getBoundingClientRect();
      const headerTop = window.pageYOffset + headerRect.top;
      const scrollPosition = headerTop - 10;
      
      window.scrollTo({
        top: scrollPosition,
        behavior: 'smooth'
      });
    } else {
      scrollToBottom();
    }
  };

  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.type === 'user') {
        scrollToBottom();
      }
    }
  }, [messages]);

  useEffect(() => {
    if (loading) {
      setTimeout(() => {
        scrollToHideFooter();
      }, 100);
    }
  }, [loading]);

  useEffect(() => {
    fetchAvailableDocuments();
  }, []);

  // Monitor conversation capacity
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    
    const totalTokens = messages.reduce((total, msg) => {
      if (msg.tokens?.total) {
        return total + msg.tokens.total;
      }
      return total + Math.ceil((msg.content || '').length / 3.5);
    }, 0);
    
    const CONTEXT_WINDOW_LIMIT = 200000;
    const usagePercent = (totalTokens / CONTEXT_WINDOW_LIMIT) * 100;
    
    if (usagePercent >= 95) {
      setIsConversationAtCapacity(true);
    } else {
      setIsConversationAtCapacity(false);
    }
  }, [messages]);

  // Fixed Conversation Search Function - Uses actual search API
  const handleConversationSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearchLoading(true);
    setSearchResults(null);
    
    try {
      const token = localStorage.getItem('token');
      
      // Call the actual search API endpoint
      const response = await fetch(`/api/conversations/search?query=${encodeURIComponent(searchQuery)}&limit=20`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Search results:', data);
        
        setSearchResults({
          query: data.query,
          results: data.results,
          totalFound: data.totalFound,
          timestamp: new Date()
        });
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Search failed' }));
        setError('Search failed: ' + (errorData.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Search error:', error);
      setError('Search failed: ' + error.message);
    } finally {
      setSearchLoading(false);
    }
  };

  // Clear search results
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
    setShowSearchModal(false);
  };

  const isImageDocument = (doc) => {
    if (!doc) return false;
    const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    return doc.mimetype && imageTypes.includes(doc.mimetype.toLowerCase());
  };

  const isPdfWithPages = (doc) => {
    if (!doc) return false;
    return doc.mimetype === "application/pdf" && doc.pageImages && doc.pageImages.length > 0;
  };

  const isDisplayableDocument = (doc) => {
    return isImageDocument(doc) || doc.mimetype === "application/pdf";
  };

  const extractImageReferences = (content) => {
    console.log('Extracting image references from:', content.substring(0, 100));
    const imageRefs = [];
    const patterns = [
      /document ID:\s*([a-f0-9]{24})/gi,
      /image document\s*([a-f0-9]{24})/gi,
      /\[Image:\s*([a-f0-9]{24})\]/gi,
      /Document \d+:\s*[^*\n]+\*\*[^]*?Document ID:\s*([a-f0-9]{24})/gi,
      /(\w+\.(?:jpg|jpeg|png|gif|pdf))\s*\(Document ID:\s*([a-f0-9]{24})\)/gi,
    ];
    
    patterns.forEach(pattern => {
      let match;
      while ((match = pattern.exec(content)) !== null) {
        const docId = match[2] || match[1];
        if (docId && docId.match(/^[a-f0-9]{24}$/)) {
          console.log('Found document ID:', docId);
          imageRefs.push(docId);
        }
      }
    });
    
    const uniqueRefs = [...new Set(imageRefs)];
    console.log('Total unique image refs found:', uniqueRefs.length);
    return uniqueRefs;
  };

  const fetchImageDocument = async (docId) => {
    console.log('Fetching image document:', docId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/documents/${docId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const doc = await response.json();
        console.log('Document fetched:', doc.originalName, 'Type:', doc.mimetype);
        if (isDisplayableDocument(doc)) {
          console.log('Document is displayable (image or PDF), adding to cache');
          setReferencedImages(prev => ({
            ...prev,
            [docId]: doc
          }));
          return doc;
        } else {
          console.log('Document is not displayable');
        }
      }
    } catch (error) {
      console.error('Error fetching image document:', error);
    }
    return null;
  };

  const processMessageForImages = async (content) => {
    console.log('Processing message for images...');
    const imageRefs = extractImageReferences(content);
    for (const docId of imageRefs) {
      if (!referencedImages[docId]) {
        await fetchImageDocument(docId);
      }
    }
  };

  // Enhanced genealogy-specific token estimation
  const calculateTimeout = (queryText, requestType) => {
    const estimatedTokens = Math.ceil(queryText.length / 3);
    
    let timeoutMs = 180000; // Default 3 minutes
    
    if (requestType === 'book' || estimatedTokens >= 15000 || queryText.length > 45000) {
      timeoutMs = 1800000; // 30 minutes for book-level requests
      console.log('Book-level request detected: 30 minute timeout set');
    } else if (requestType === 'chapter' || estimatedTokens >= 5000 || queryText.length > 15000) {
      timeoutMs = 300000; // 5 minutes for chapter-level requests
      console.log('Chapter-level request detected: 5 minute timeout set');
    } else if (estimatedTokens >= 3000 || queryText.length > 9000) {
      timeoutMs = 180000; // 3 minutes for detailed requests
      console.log('Detailed request detected: 3 minute timeout set');
    }
    
    console.log(`Query analysis - Length: ${queryText.length} chars, Est. tokens: ${estimatedTokens}, Type: ${requestType}, Timeout: ${timeoutMs/1000}s (${Math.round(timeoutMs/60000)} min)`);
    return timeoutMs;
  };

  // Enhanced genealogy-specific token estimation for better accuracy
  const estimateGenealogyTokens = (message, requestType) => {
    const baseTokens = Math.ceil(message.length / 3.5);
    
    // GENEALOGY-SPECIFIC MULTIPLIERS
    let multiplier = 1.0;
    
    // Genealogy prompts are longer and more complex
    if (requestType === 'book') {
      multiplier = 15.0; // Book-level genealogy analysis
    } else if (requestType === 'chapter') {
      multiplier = 4.0; // Chapter-level genealogy analysis  
    } else {
      // Regular genealogy requests still need more tokens than simple chat
      multiplier = 2.5; // Enhanced genealogy responses
    }
    
    const estimatedTokens = Math.round(baseTokens * multiplier);
    console.log(`Genealogy token estimation - Base: ${baseTokens}, Type: ${requestType}, Multiplier: ${multiplier}x, Estimated: ${estimatedTokens}`);
    
    return estimatedTokens;
  };

  const getGeneErrorMessage = (response, errorData) => {
    if (response.status === 529 || (errorData.error && errorData.error.type === 'overloaded_error')) {
      return "Gene is experiencing high demand right now. Please wait 5-10 minutes and try your request again.";
    }
    
    switch (response.status) {
      case 401:
        return "Your session has expired. Please refresh the page and log in again.";
      case 403:
        return "Access denied. Please check your permissions and try again.";
      case 408:
        return "Your request timed out. Please try again or break your request into smaller parts.";
      case 413:
        return "Your request is too large. Please try breaking it into smaller parts.";
      case 429:
        return "Gene is receiving too many requests. Please wait a moment and try again.";
      case 500:
      case 502:
      case 503:
      case 504:
        return "Gene is temporarily unavailable. Please try again in a few minutes.";
      default:
        return "Gene encountered an issue processing your request. Please try again or break your request into smaller parts.";
    }
  };

  const makeResearchRequest = async (conversationId, queryText, selectedDocuments, token, requestType) => {
    const timeoutMs = calculateTimeout(queryText, requestType);
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.log('Request timeout reached, aborting...');
      controller.abort();
    }, timeoutMs);
    
    try {
      console.log('Step 2: Calling research API...');
      console.log(`Timeout set to ${timeoutMs/1000} seconds (${Math.round(timeoutMs/60000)} minutes)`);
      
      const payload = {
        query: queryText,
        documentIds: selectedDocuments.filter(id => !sentDocuments.includes(id)),
        activeTreeId: props.activeTreeId || null,
        conversationId: conversationId
      };
      console.log('API Payload:', JSON.stringify(payload, null, 2));
      
      const response = await fetch('/api/research', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
        signal: controller.signal
      });

      console.log('Research API response status:', response.status);
      
      clearTimeout(timeoutId);
      
      return response;
      
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        console.error('Request timed out after', timeoutMs/1000, 'seconds');
        throw new Error(`Your request took longer than ${Math.round(timeoutMs/60000)} minutes to process. This can happen with very large or complex queries. Please try breaking your request into smaller parts or be more specific.`);
      }
      
      throw error;
    }
  };

  const fetchAvailableDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/documents/available/for-analysis', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Available documents loaded:', data.length);
        setAvailableDocuments(data);
      }
    } catch (error) {
      console.error('Error fetching available documents:', error);
    }
  };

  const determineRequestType = (message) => {
    const lowerMessage = message.toLowerCase();
    const bookKeywords = ['book', 'write a book', 'family history book', 'autobiography', 'memoir', 'complete family history'];
    const chapterKeywords = ['chapter', 'story', 'detailed story', 'narrative', 'full story', 'write about'];
    
    if (bookKeywords.some(keyword => lowerMessage.includes(keyword))) {
      return 'book';
    } else if (chapterKeywords.some(keyword => lowerMessage.includes(keyword))) {
      return 'chapter';
    }
    return 'regular';
  };

  useEffect(() => {
    fetchConversations();
  }, []);

  // Added limit parameter to fetch ALL conversations (not just 20)
  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/conversations?limit=1000', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setConversations(data);
        } else if (data.conversations) {
          setConversations(data.conversations);
        }
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setError('Gene is having trouble loading your conversations. Please refresh the page and try again.');
    }
  };

  const createNewConversation = async () => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/conversations', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: `Research Session ${new Date().toLocaleString()}`
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Create conversation response:', data);
        
        let conversation = data.conversation || data;
        
        if (conversation) {
          setCurrentConversation(conversation);
          setSentDocuments([]);
          setMessages([]);
          setSelectedDocuments([]);
          fetchConversations();
          return conversation._id || conversation.id;
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        setError(getGeneErrorMessage(response, errorData));
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      setError('Gene is having trouble creating a new conversation. Please try again.');
    }
    return null;
  };

  const loadConversation = async (conversationId) => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/conversations/${conversationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCurrentConversation(data.conversation || data);
        const loadedMessages = data.messages || data.conversation?.messages || [];
        setMessages(loadedMessages);
        setSelectedDocuments([]);
        
        for (const message of loadedMessages) {
          if (message.type === 'assistant') {
            await processMessageForImages(message.content);
          }
        }
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
      setError('Gene is having trouble loading this conversation. Please try again.');
    }
  };

  const toggleDocumentSelection = (docId) => {
    console.log('Toggling document:', docId);
    setSelectedDocuments(prev => {
      const newSelection = prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId];
      console.log('Selected documents after toggle:', newSelection);
      const selectedNames = availableDocuments
        .filter(doc => newSelection.includes(doc._id))
        .map(doc => doc.originalName);
      console.log('Selected document names:', selectedNames);
      return newSelection;
    });
  };

  const renameConversation = async (conversationId, newTitle) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/conversations/${conversationId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ title: newTitle })
      });
      
      if (response.ok) {
        setConversations(prev => 
          prev.map(conv => 
            (conv._id || conv.id) === conversationId 
              ? { ...conv, title: newTitle } 
              : conv
          )
        );
        
        if ((currentConversation?._id || currentConversation?.id) === conversationId) {
          setCurrentConversation(prev => ({ ...prev, title: newTitle }));
        }
        
        setEditingConversationId(null);
        setEditingTitle('');
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        setError(getGeneErrorMessage(response, errorData));
      }
    } catch (error) {
      console.error('Error renaming conversation:', error);
      setError('Gene is having trouble renaming this conversation. Please try again.');
    }
  };

  const deleteConversation = async (conversationId) => {
    if (!window.confirm('Are you sure you want to delete this conversation? This action cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        setConversations(prev => prev.filter(conv => (conv._id || conv.id) !== conversationId));
        
        if ((currentConversation?._id || currentConversation?.id) === conversationId) {
          setCurrentConversation(null);
                  setSentDocuments([]);
          setMessages([]);
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        setError(getGeneErrorMessage(response, errorData));
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      setError('Gene is having trouble deleting this conversation. Please try again.');
    }
  };

  const startEditing = (conversationId, currentTitle) => {
    setEditingConversationId(conversationId);
    setEditingTitle(currentTitle);
  };

  const cancelEditing = () => {
    setEditingConversationId(null);
    setEditingTitle('');
  };

  const handleEditSubmit = (conversationId) => {
    if (editingTitle.trim()) {
      renameConversation(conversationId, editingTitle.trim());
    } else {
      cancelEditing();
    }
  };

  const PDFPageViewer = ({ docId, doc, token }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const totalPages = doc.pageImages ? doc.pageImages.length : 0;
    
    const handlePrevPage = () => {
      if (currentPage > 1) setCurrentPage(currentPage - 1);
    };
    
    const handleNextPage = () => {
      if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };
    
    return (
      <div style={{ 
        border: '1px solid #ddd', 
        borderRadius: '8px', 
        padding: '1rem',
        backgroundColor: '#f9f9f9'
      }}>
        <div style={{ marginBottom: '0.5rem', fontWeight: 'bold' }}>
          {doc.originalName}
        </div>
        
        <div style={{ position: 'relative' }}>
          <img
            src={`/api/documents/${docId}/page/${currentPage}?token=${localStorage.getItem("token")}`}
            alt={`${doc.originalName} - Page ${currentPage}`}
            style={{
              maxWidth: '100%',
              maxHeight: '600px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              display: 'block',
              margin: '0 auto'
            }}
            onError={(e) => {
              console.error('Failed to load PDF page:', currentPage);
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNiIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkZhaWxlZCB0byBsb2FkIHBhZ2U8L3RleHQ+PC9zdmc+';
            }}
          />
        </div>
        
        {totalPages > 1 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            marginTop: '1rem',
            gap: '1rem'
          }}>
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: currentPage === 1 ? '#ccc' : '#2e7d32',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              Previous
            </button>
            
            <span style={{ fontWeight: 'bold' }}>
              Page {currentPage} of {totalPages}
            </span>
            
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: currentPage === totalPages ? '#ccc' : '#2e7d32',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderMessageContent = (content) => {
    console.log('Rendering message content');
    
    const stripDocumentIds = (text) => {
      return text.replace(/\s*\(Document ID:\s*[a-f0-9]{24}\)/gi, '');
    };
    
    const displayContent = stripDocumentIds(content);
    const imageRefs = extractImageReferences(content);
    console.log('Found image refs:', imageRefs);
    
    if (imageRefs.length === 0) {
      console.log('No image references found, returning plain content');
      return (
        <ReactMarkdown 
          remarkPlugins={[remarkGfm]}
          components={markdownComponents}
        >
          {displayContent}
        </ReactMarkdown>
      );
    }
    
    let processedContent = displayContent;
    const elements = [];
    let lastIndex = 0;
    
    imageRefs.forEach((docId, index) => {
      const doc = referencedImages[docId];
      console.log(`Checking image ${docId}:`, doc ? doc.originalName : 'Not in cache');
      
      if (doc && isDisplayableDocument(doc)) {
        console.log('Rendering displayable document:', doc.originalName);
        
        const patterns = [
          new RegExp(`document ID:\\s*${docId}`, 'gi'),
          new RegExp(`image document\\s*${docId}`, 'gi'),
          new RegExp(`\\[Image:\\s*${docId}\\]`, 'gi'),
          new RegExp(`([\\w.-]+\\.(?:jpg|jpeg|png|gif|pdf))\\s*\\(Document ID:\\s*${docId}\\)`, 'gi')
        ];
        
        patterns.forEach(pattern => {
          const match = pattern.exec(content);
          if (match) {
            const beforeMatch = content.substring(0, match.index);
            const strippedBeforeMatch = stripDocumentIds(beforeMatch);
            const beforeText = processedContent.substring(lastIndex, strippedBeforeMatch.length);
            
            if (beforeText) {
              elements.push(
                <ReactMarkdown 
                  key={`text-${index}-${lastIndex}`}
                  remarkPlugins={[remarkGfm]}
                  components={markdownComponents}
                >
                  {beforeText}
                </ReactMarkdown>
              );
            }
            
            if (isPdfWithPages(doc)) {
              console.log(`Rendering PDF with ${doc.pageImages.length} pages`);
              
              elements.push(
                <div key={`pdf-${docId}`} style={{ margin: '1rem 0' }}>
                  <PDFPageViewer 
                    docId={docId} 
                    doc={doc} 
                    token={localStorage.getItem('token')} 
                  />
                </div>
              );
              
              const matchText = match[0];
              const strippedMatchText = stripDocumentIds(matchText);
              lastIndex = strippedBeforeMatch.length + strippedMatchText.length;
              return;
            }
            
            // Show PDFs without pages as document preview
            if (doc.mimetype === 'application/pdf') {
              console.log('PDF without pages, showing as document preview:', doc.originalName);
              
              elements.push(
                <div key={`pdf-preview-${docId}`} style={{ 
                  margin: '1rem 0',
                  padding: '1rem',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  backgroundColor: '#f9f9f9'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '24px', marginRight: '10px' }}>📄</span>
                    <strong>{doc.originalName}</strong>
                  </div>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    PDF Document • {doc.extractedText ? `${doc.extractedText.split(' ').length} words` : 'Text preview not available'}
                  </div>
                  {doc.extractedText ? (
                    <div style={{ 
                      marginTop: '10px',
                      padding: '10px',
                      backgroundColor: 'white',
                      borderRadius: '4px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      fontSize: '13px',
                      lineHeight: '1.5'
                    }}>
                      {doc.extractedText.substring(0, 500)}
                      {doc.extractedText.length > 500 && '...'}
                    </div>
                  ) : (
                    <div style={{
                      marginTop: '10px',
                      padding: '10px',
                      backgroundColor: '#f0f0f0',
                      borderRadius: '4px',
                      fontSize: '13px',
                      color: '#666',
                      textAlign: 'center'
                    }}>
                      Document content is being analyzed by Gene
                    </div>
                  )}
                </div>
              );
              
              const matchText = match[0];
              const strippedMatchText = stripDocumentIds(matchText);
              lastIndex = strippedBeforeMatch.length + strippedMatchText.length;
              return;
            }
            
            console.log('Rendering inline image:', doc.originalName);
            
            elements.push(
              <div key={`img-${docId}`} style={{ margin: '1rem 0' }}>
                <img
                  src={`/api/documents/${docId}/view?token=${localStorage.getItem('token')}`}
                  alt={doc.originalName}
                  style={{
                    maxWidth: '400px',
                    maxHeight: '300px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'block'
                  }}
                  onClick={() => setViewingImage(doc)}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    console.error('Failed to load image:', docId);
                  }}
                />
                <p style={{ fontSize: '12px', color: '#666', marginTop: '0.25rem' }}>
                  {doc.originalName} (Click to enlarge)
                </p>
              </div>
            );
            
            const matchText = match[0];
            const strippedMatchText = stripDocumentIds(matchText);
            lastIndex = strippedBeforeMatch.length + strippedMatchText.length;
          }
        });
      } else {
        console.log('Document not in cache or not displayable');
      }
    });
    
    if (lastIndex < displayContent.length) {
      elements.push(
        <ReactMarkdown 
          key={`text-final`}
          remarkPlugins={[remarkGfm]}
          components={markdownComponents}
        >
          {displayContent.substring(lastIndex)}
        </ReactMarkdown>
      );
    }
    
    console.log('Rendered elements:', elements.length);
    return elements.length > 0 ? <>{elements}</> : (
      <ReactMarkdown 
        remarkPlugins={[remarkGfm]}
        components={markdownComponents}
      >
        {displayContent}
      </ReactMarkdown>
    );
  };

  const handleSendMessage = async () => {
    console.log('Send button clicked!', { 
      newMessage, 
      loading, 
      currentConversation: currentConversation?._id,
      selectedDocuments,
      selectedDocumentNames: availableDocuments
        .filter(doc => selectedDocuments.includes(doc._id))
        .map(doc => doc.originalName)
    });
    
    if (!newMessage.trim()) {
      console.log('Message is empty, returning');
      return;
    }
    
    console.log('Passed empty check, proceeding...');

    // IMPROVED CLAUDE AI-STYLE INTELLIGENT CONVERSATION LIMITS
    const calculateConversationTokens = () => {
      return messages.reduce((total, msg) => {
        // Use actual token counts from API responses
        if (msg.tokens?.total) {
          return total + msg.tokens.total;
        }
        // Fallback: estimate tokens for messages without token data
        return total + Math.ceil(msg.content.length / 3.5);
      }, 0);
    };

    // Use genealogy-specific token estimation for current message
    const reqType = determineRequestType(newMessage);
    setRequestType(reqType);
    const estimatedCurrentTokens = estimateGenealogyTokens(newMessage, reqType);
    const totalConversationTokens = calculateConversationTokens() + estimatedCurrentTokens;

    // Claude Sonnet 4 context window limits (conservative estimates)
    const CONTEXT_WINDOW_LIMIT = 200000; // ~200K tokens (leave buffer for safety)
    const contextUsagePercent = (totalConversationTokens / CONTEXT_WINDOW_LIMIT) * 100;

    // Better monthly token balance retrieval
    const getLastTokenBalance = () => {
      const lastAssistantMessage = [...messages].reverse().find(msg => msg.type === 'assistant' && msg.tokenBalance);
      if (lastAssistantMessage?.tokenBalance?.remaining !== undefined) {
        return lastAssistantMessage.tokenBalance.remaining;
      }
      return null;
    };

    const remainingMonthlyTokens = getLastTokenBalance();
    const hasLowMonthlyTokens = remainingMonthlyTokens !== null && remainingMonthlyTokens < 10000;

    // Progressive warnings system (like Claude AI)
    if (contextUsagePercent > 95) {
      setError(`This conversation has reached Gene's maximum capacity (${Math.round(contextUsagePercent)}% of ${CONTEXT_WINDOW_LIMIT.toLocaleString()} tokens). Please start a new conversation to continue.\n\nConversation tokens: ${totalConversationTokens.toLocaleString()}`);
      return;
    } else if (contextUsagePercent > 90) {
      setError(`This conversation is nearly at capacity (${Math.round(contextUsagePercent)}% of context window). Consider starting a new conversation soon.\n\nTokens used: ${totalConversationTokens.toLocaleString()} / ${CONTEXT_WINDOW_LIMIT.toLocaleString()}`);
      // Don't return - allow this message but warn user
    } else if (contextUsagePercent > 85) {
      console.log(`Conversation approaching limits: ${Math.round(contextUsagePercent)}% of context window used`);
    }

    // Trial vs Professional Token Exhaustion Flows
    if (remainingMonthlyTokens !== null && remainingMonthlyTokens <= 0) {
      
      // TRIAL USER FLOW - Trial expired, must subscribe
      if (user && user.subscription && user.subscription.plan === 'trial') {
        setError(
          `Your Free Trial Has Ended!\n\n` +
          `You've successfully used all 250,000 trial tokens exploring your family history. ` +
          `Ready to unlock unlimited genealogy research?\n\n` +
          `Upgrade to Professional Plan:\n` +
          `• 1,000,000 tokens per month\n` +
          `• Advanced family tree analysis\n` +
          `• Historical document insights\n` +
          `• DNA connection research\n\n` +
          `Subscribe now for just $100/month and continue your journey!\n\n` +
          `[Visit Pricing Page to Subscribe]`
        );
        return;
      }
      
      // PROFESSIONAL USER FLOW - Monthly tokens exhausted, offer overage
      else if (user && user.subscription && user.subscription.plan === 'professional') {
        setError(
          `Monthly Token Limit Reached\n\n` +
          `You've used your 1,000,000 monthly tokens. Continue your genealogy research ` +
          `with additional tokens?\n\n` +
          `Additional Token Pricing:\n` +
          `• $5.00 per 100,000 tokens\n` +
          `• Charged automatically to your account\n` +
          `• No interruption to your research\n\n` +
          `This request needs approximately ${estimatedCurrentTokens.toLocaleString()} tokens ` +
          `(~$${((estimatedCurrentTokens / 100000) * 5).toFixed(2)}).\n\n` +
          `By continuing, you agree to the $5.00 charge for up to 100,000 additional tokens.\n\n` +
          `[Contact support if you have questions about overage billing]`
        );
        return;
      }
      
      // FALLBACK - Unknown plan type
      else {
        setError(
          `You don't have enough tokens remaining for this request. ` +
          `You have ${remainingMonthlyTokens.toLocaleString()} tokens left, but genealogy ` +
          `requests typically need ${estimatedCurrentTokens.toLocaleString()} tokens. ` +
          `Please upgrade your subscription or wait for your monthly reset.`
        );
        return;
      }
    } 
    
    // Low token warnings with plan-specific guidance
    else if (hasLowMonthlyTokens && remainingMonthlyTokens < estimatedCurrentTokens) {
      
      if (user && user.subscription && user.subscription.plan === 'trial') {
        setError(
          `Trial tokens running low! You have ${remainingMonthlyTokens.toLocaleString()} tokens left, ` +
          `but this genealogy request may need approximately ${estimatedCurrentTokens.toLocaleString()} tokens.\n\n` +
          `Consider upgrading to Professional for 1,000,000 monthly tokens and unlimited research!`
        );
        return;
      }
      
      else if (user && user.subscription && user.subscription.plan === 'professional') {
        setError(
          `Monthly tokens running low! You have ${remainingMonthlyTokens.toLocaleString()} tokens left, ` +
          `but this ${reqType} genealogy request may need approximately ${estimatedCurrentTokens.toLocaleString()} tokens.\n\n` +
          `Additional tokens available for $5.00 per 100,000 when needed.`
        );
        return;
      }
      
      else {
        setError(
          `You may not have enough tokens for this ${reqType} genealogy request. ` +
          `You have ${remainingMonthlyTokens.toLocaleString()} tokens left, but this request ` +
          `may need approximately ${estimatedCurrentTokens.toLocaleString()} tokens. ` +
          `Consider a shorter request or upgrade your subscription.`
        );
        return;
      }
    } 
    
    // Low token console warnings
    else if (hasLowMonthlyTokens && remainingMonthlyTokens < 5000) {
      const planType = user?.subscription?.plan || 'unknown';
      console.log(`User (${planType} plan) has low monthly tokens: ${remainingMonthlyTokens.toLocaleString()} remaining`);
    }

    console.log(`Token analysis - Conversation: ${totalConversationTokens.toLocaleString()} tokens (${Math.round(contextUsagePercent)}% of context) | Monthly remaining: ${remainingMonthlyTokens?.toLocaleString() || 'Unknown'} | Estimated needed: ${estimatedCurrentTokens.toLocaleString()}`);

    let conversationId = currentConversation?._id || currentConversation?.id;
    
    if (!conversationId) {
      console.log('Creating new conversation...');
      conversationId = await createNewConversation();
      if (!conversationId) return;
    }

    console.log('Using conversationId:', conversationId);
    console.log('Selected documents:', selectedDocuments);
    console.log('Number of selected documents:', selectedDocuments.length);

    const userMessage = {
      type: 'user',
      content: newMessage,
      timestamp: new Date(),
      requestType: reqType,
      documents: selectedDocuments.length > 0 ? selectedDocuments : null
    };
    setMessages(prev => [...prev, userMessage]);
    const queryText = newMessage;
    setNewMessage('');
    setLoading(true);
    setError(null);

    console.log('About to make API calls...');

    try {
      const token = localStorage.getItem('token');
      
      console.log('Step 1: Saving user message to conversation...');
      await fetch(`/api/conversations/${conversationId}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'user',
          content: queryText,
          requestType: reqType
        })
      });

      const response = await makeResearchRequest(conversationId, queryText, selectedDocuments, token, reqType);

      if (response.ok) {
        const data = await response.json();
        // Mark attached documents as sent - they now live in conversation history (cached)
        if (selectedDocuments.length > 0) {
          setSentDocuments(prev => [...new Set([...prev, ...selectedDocuments])]);
        }
        console.log('Research API response data:', data);
        console.log('Token Balance received:', data.tokenBalance);
        console.log('Token Breakdown:', data.tokenBalance?.breakdown);
        
        await processMessageForImages(data.response);
        
        // Enhanced token tracking with better data structure
        const assistantMessage = {
          type: 'assistant',
          content: data.response,
          requestType: reqType,
          tokens: data.tokenBalance ? {
            input: data.tokenBalance.breakdown?.input || 0,
            output: data.tokenBalance.breakdown?.output || 0,
            total: data.tokenBalance.used || 0,
            saved: data.tokenBalance.breakdown?.saved || 0,
            savingsPercentage: data.tokenBalance.breakdown?.savingsPercentage || 0,
            max: reqType === 'book' ? 60000 : reqType === 'chapter' ? 15000 : 6000
          } : { input: 0, output: 0, total: 0, saved: 0, savingsPercentage: 0, max: 6000 },
          tokenBalance: data.tokenBalance ? {
            remaining: data.tokenBalance.remaining
          } : null,
          cost: 0
        };

        console.log('Assistant message tokens:', assistantMessage.tokens);
        console.log('Assistant token balance:', assistantMessage.tokenBalance);

        console.log('Step 4: Saving assistant response to conversation...');
        await fetch(`/api/conversations/${conversationId}/messages`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(assistantMessage)
        });

        console.log('Step 5: Adding assistant response to UI...');
        setMessages(prev => [...prev, {
          type: 'assistant',
          content: data.response,
          timestamp: new Date(),
          tokens: assistantMessage.tokens,
          tokenBalance: assistantMessage.tokenBalance,
          cost: assistantMessage.cost,
          requestType: reqType
        }]);
        
        setShowDocumentSelector(false);
        
        fetchConversations();
        console.log('All steps completed successfully!');
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Research API error:', errorData);
        setError(getGeneErrorMessage(response, errorData));
      }
    } catch (error) {
      console.error('Error in handleSendMessage:', error);
      setError(error.message || 'Gene encountered an issue processing your request. Please try again.');
    } finally {
      setLoading(false);
      console.log('handleSendMessage completed, loading set to false');
    }
  };

  const getRequestTypeIndicator = (type) => {
    switch(type) {
      case 'book': return { icon: 'Book', text: 'Book-level request (60k tokens)', color: '#ffffff' };
      case 'chapter': return { icon: 'Book', text: 'Chapter-level request (15k tokens)', color: '#daa520' };
      default: return { icon: 'Flash', text: 'Regular request (6k tokens)', color: '#2e7d32' };
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    document.body.style.overflow = isFullscreen ? "hidden" : "auto";
  };

  const conversationIdForMap = currentConversation?._id || currentConversation?.id;

  return (
    <div className={`chat-interface ${isFullscreen ? "fullscreen" : ""}`}>
      <div className={`conversation-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-header-content">
            {!sidebarCollapsed && (
              <div>
                <h3 style={{margin: '0 0 0.5rem'}}>Conversations</h3>
                <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
                  <button onClick={createNewConversation} className="new-conversation-btn">
                    + New
                  </button>
                  <button 
                    onClick={() => setShowSearchModal(true)} 
                    className="search-conversation-btn"
                    style={{
                      background: '#2e7d32',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                    title="Search conversations"
                  >
                    Search
                  </button>
                </div>
              </div>
            )}
            <button 
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)} 
              className="collapse-sidebar-btn"
              title={sidebarCollapsed ? "Expand conversations" : "Collapse conversations"}
              style={{
                position: 'absolute',
                top: '0.5rem',
                right: '0.5rem'
              }}
            >
              {sidebarCollapsed ? '→' : '←'}
            </button>
          </div>
        </div>
        {!sidebarCollapsed && (
          <div className="conversation-list">
            {conversations.map(conversation => {
              const conversationId = conversation.id || conversation._id;
              const isActive = (currentConversation?._id || currentConversation?.id) === conversationId;
              const isEditing = editingConversationId === conversationId;
              
              return (
                <div 
                  key={conversationId}
                  className={`conversation-item ${isActive ? 'active' : ''}`}
                >
                  {isEditing ? (
                    <div className="conversation-edit-container">
                      <input
                        type="text"
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleEditSubmit(conversationId);
                          } else if (e.key === 'Escape') {
                            cancelEditing();
                          }
                        }}
                        onBlur={() => handleEditSubmit(conversationId)}
                        className="conversation-edit-input"
                        autoFocus
                      />
                    </div>
                  ) : (
                    <>
                      <div 
                        className="conversation-content"
                        onClick={() => loadConversation(conversationId)}
                      >
                        <div className="conversation-title">{conversation.title}</div>
                        <div className="conversation-date">
                          {new Date(conversation.lastActivity).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="conversation-actions">
                        <button
                          className="conversation-action-btn edit-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditing(conversationId, conversation.title);
                          }}
                          title="Rename conversation"
                        >
                          ✎
                        </button>
                        <button
                          className="conversation-action-btn delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteConversation(conversationId);
                          }}
                          title="Delete conversation"
                        >
                          ×
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {sidebarCollapsed && (
          <div className="collapsed-new-conversation">
            <button 
              onClick={createNewConversation} 
              className="collapsed-new-btn"
              title="New conversation"
            >
              +
            </button>
            <button 
              onClick={() => setShowSearchModal(true)} 
              className="collapsed-search-btn"
              title="Search conversations"
              style={{
                background: '#2e7d32',
                color: 'white',
                border: 'none',
                padding: '0.5rem',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                marginTop: '0.5rem',
                width: '100%'
              }}
            >
              Search
            </button>
          </div>
        )}
      </div>
      
      <div className="chat-main">
        <div className="chat-header">
          <GeneLogo size="normal" showTagline={true} />
          <button className="fullscreen-toggle-btn"
            onClick={toggleFullscreen}
            style={{
              position: "absolute",
              right: "1rem",
              top: "4rem",
              padding: "0.5rem 1rem",
              background: "#4caf50",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              zIndex: 9999
            }}
          >
            {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          </button>
          {currentConversation && (
            <div className="conversation-info">
              <span>Conversation: {currentConversation.title}</span>
            </div>
          )}
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
        </div>
        
        <div className="chat-messages">
          {messages.length === 0 && (
            <div className="welcome-message">
              <h3>Welcome to Gene!</h3>
              <p>Start a conversation about genealogy research. Ask me about methods, sources, or any family history questions!</p>
              <p><strong>Tip:</strong> Click "New" to create your first conversation, then type your question below.</p>
              {availableDocuments.length > 0 && (
                <div style={{marginTop: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6'}}>
                  <p><strong>You have {availableDocuments.length} document(s) ready for analysis!</strong></p>
                  <p>Use the document selector when asking questions to include your uploaded genealogy documents.</p>
                  <p><strong>Images and PDFs are now supported!</strong> Upload photos and PDFs, and Gene can display them during conversations. Type your question below.</p>
                </div>
              )}
              <div style={{marginTop: '1rem', padding: '1rem', background: '#e8f5e9', borderRadius: '8px', border: '1px solid #4caf50'}}>
                <p><strong>New: Search Your Conversations!</strong></p>
                <p>Click the search button to find information from your previous conversations about family names, research findings, and genealogy insights.</p>
              </div>
            </div>
          )}
          
          {messages.map((message, index) => (
            <div key={index} className={`message ${message.type}`}>
              <div className="message-content">
                {message.type === 'user' && message.documents && message.documents.length > 0 && (
                  <div className="message-documents" style={{marginBottom: '0.5rem', padding: '0.5rem', background: '#e8f4fd', borderRadius: '4px', fontSize: '12px'}}>
                    Documents included: {message.documents.length} file(s)
                  </div>
                )}
                <div>{renderMessageContent(message.content)}</div>
                {message.requestType && (
                  <div className="request-type-indicator">
                    <span style={{color: getRequestTypeIndicator(message.requestType).color}}>
                      {getRequestTypeIndicator(message.requestType).icon} {getRequestTypeIndicator(message.requestType).text}
                    </span>
                  </div>
                )}
                {console.log('Displaying message:', message.type, 'tokens:', message.tokens, 'balance:', message.tokenBalance)}
                {message.tokens && (
                  <div className="message-metadata">
                    <span>
                      Tokens: {message.tokens.total || (message.tokens.input + message.tokens.output)} 
                      {message.tokenBalance && message.tokenBalance.remaining !== undefined && (
                        <> | Balance: {message.tokenBalance.remaining.toLocaleString()}</>
                      )}
                    </span>
                  </div>
                )}
                {message.type === 'assistant' && currentConversation && message.content && message.content.length > 1000 && (
                  <DownloadButtons 
                    conversationId={currentConversation._id || currentConversation.id}
                    messageContent={message.content}
                    requestType={message.requestType}
                    imageCache={referencedImages}
                  />
                )}
              </div>
              <div className="message-timestamp">
                {new Date(message.timestamp).toLocaleTimeString()}
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="message assistant">
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <div className="request-type-indicator">
                  <span style={{color: getRequestTypeIndicator(requestType).color}}>
                    {getRequestTypeIndicator(requestType).icon} {getRequestTypeIndicator(requestType).text}
                  </span>
                  {requestType === 'book' && (
                    <div style={{fontSize: '12px', color: '#666', marginTop: '0.25rem'}}>
                      Large requests may take up to 30 minutes - please be patient
                    </div>
                  )}
                  {requestType === 'chapter' && (
                    <div style={{fontSize: '12px', color: '#666', marginTop: '0.25rem'}}>
                      Processing may take up to 5 minutes
                    </div>
                  )}
                </div>
                {selectedDocuments.length > 0 && (
                  <div style={{fontSize: '12px', color: '#666', marginTop: '0.5rem'}}>
                    Analyzing {selectedDocuments.length} document(s)...
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        {viewingImage && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 999900,
              padding: '2rem'
            }}
            onClick={() => setViewingImage(null)}
          >
            <div style={{position: 'relative', maxWidth: '90%', maxHeight: '90%'}}>
              <img 
                src={`/api/documents/${viewingImage._id}/view?token=${localStorage.getItem('token')}`}
                alt={viewingImage.originalName}
                style={{
                  maxWidth: '100%',
                  maxHeight: '90vh',
                  objectFit: 'contain'
                }}
              />
              <button
                onClick={() => setViewingImage(null)}
                style={{
                  position: 'absolute',
                  top: '-40px',
                  right: 0,
                  background: 'white',
                  color: 'black',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Close
              </button>
              <div style={{
                position: 'absolute',
                bottom: '-40px',
                left: 0,
                right: 0,
                textAlign: 'center',
                color: 'white',
                fontSize: '14px'
              }}>
                {viewingImage.originalName}
              </div>
            </div>
          </div>
        )}
        
        <div className="chat-input">
          {availableDocuments.length > 0 && (
            <div className="document-selector-container">
              <button 
                onClick={() => setShowDocumentPopup(true)}
                className="document-selector-toggle"
                style={{
                  background: selectedDocuments.length > 0 ? '#2e7d32' : '#f8f9fa',
                  color: selectedDocuments.length > 0 ? 'white' : '#333',
                  border: '1px solid #dee2e6',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  marginBottom: '0.5rem'
                }}
              >
                Documents ({selectedDocuments.length}/{availableDocuments.length})
              </button>
              
              {/* Display selected documents */}
              {selectedDocuments.length > 0 && (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.3rem',
                  marginTop: '0.5rem',
                  marginBottom: '0.5rem'
                }}>
                  {availableDocuments
                    .filter(doc => selectedDocuments.includes(doc._id))
                    .map(doc => (
                      <div key={doc._id} style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        background: '#e8f5e9',
                        border: '1px solid #4caf50',
                        borderRadius: '12px',
                        padding: '0.2rem 0.5rem',
                        fontSize: '0.8rem',
                        gap: '0.3rem',
                        maxWidth: '200px'
                      }}>
                        <span>{doc.mimetype?.includes('image') ? 'Image' : 'Document'}</span>
                        <span style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1
                        }}>
                          {doc.originalName}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDocumentSelection(doc._id);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#666',
                            cursor: 'pointer',
                            padding: '0 0.2rem',
                            fontSize: '1.2rem',
                            lineHeight: '1',
                            fontWeight: 'bold'
                          }}
                          title="Remove document"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                </div>
              )}

              {/* ✅ NEW: Family Migration Map Integration */}
              <MapIntegration 
                selectedDocuments={selectedDocuments}
                conversationId={conversationIdForMap}
                showMap={showMap}
                setShowMap={setShowMap}
              />
              
              {showDocumentSelector && (
                <div className="document-selector" style={{
                  background: 'white',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  padding: '1rem',
                  marginBottom: '0.5rem',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  <h4 style={{margin: '0 0 0.5rem', fontSize: '14px'}}>Select documents for Gene to analyze:</h4>
                  {availableDocuments.map(doc => (
                    <label key={doc._id} style={{display: 'block', marginBottom: '0.5rem', fontSize: '13px', cursor: 'pointer'}}>
                      <input
                        type="checkbox"
                        checked={selectedDocuments.includes(doc._id)}
                        onChange={() => toggleDocumentSelection(doc._id)}
                        style={{marginRight: '0.5rem'}}
                      />
                      {isImageDocument(doc) ? 'Image' : isPdfWithPages(doc) ? 'PDF' : 'Document'} {doc.originalName} ({doc.documentType})
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {!isConversationAtCapacity ? (
            <>
              <div className="input-container">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Ask Gene about your family history research..."
                  rows="3"
                  disabled={loading}
                />
                <button 
                  onClick={() => {
                    console.log('Send button clicked!', { disabled: loading || !newMessage.trim() });
                    handleSendMessage();
                  }}
                  disabled={loading || !newMessage.trim()}
                  className="send-button"
                >
                  Send
                </button>
              </div>
              <div className="request-type-preview">
                <span style={{color: getRequestTypeIndicator(determineRequestType(newMessage)).color}}>
                  {getRequestTypeIndicator(determineRequestType(newMessage)).icon} {getRequestTypeIndicator(determineRequestType(newMessage)).text}
                </span>
                {selectedDocuments.length > 0 && (
                  <span style={{marginLeft: '1rem', fontSize: '12px', color: '#666'}}>
                    {selectedDocuments.length} document(s) selected
                  </span>
                )}
              </div>
            </>
          ) : (
            <div className="conversation-at-capacity" style={{textAlign: 'center', padding: '20px'}}>
              <div style={{fontSize: '18px', color: '#ff6b6b', marginBottom: '10px'}}>
                Conversation at Maximum Capacity
              </div>
              <div style={{marginBottom: '20px'}}>
                This conversation has reached Gene's processing limit. 
                Please start a new conversation to continue your research.
              </div>
              <button 
                onClick={() => {
                  setCurrentConversation(null);
                  setSentDocuments([]);
                  setMessages([]);
                  setError(null);
                  setIsConversationAtCapacity(false);
                }}
                className="send-button"
                style={{backgroundColor: '#4CAF50'}}
              >
                Start New Conversation
              </button>
            </div>
          )}
        </div>

        {showSearchModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              zIndex: 9999000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "2rem"
            }}
            onClick={() => setShowSearchModal(false)}
          >
            <div
              style={{
                background: "white",
                borderRadius: "12px",
                padding: "2rem",
                width: "90%",
                maxWidth: "800px",
                maxHeight: "80vh",
                overflow: "auto",
                boxShadow: "0 12px 36px rgba(0,0,0,0.3)"
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  Search Conversations
                </h3>
                <button
                  onClick={() => setShowSearchModal(false)}
                  style={{
                    background: "#dc3545",
                    color: "white",
                    border: "none",
                    borderRadius: "50%",
                    width: "30px",
                    height: "30px",
                    cursor: "pointer"
                  }}
                >
                  ✕
                </button>
              </div>
              
              <div style={{ marginBottom: "1rem" }}>
                <div style={{ marginBottom: "0.5rem", fontSize: "0.9rem", color: "#666" }}>
                  Search for family names, dates, places, research findings, or any genealogy information discussed in your conversations:
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search for names, places, dates, research findings..."
                    style={{
                      flex: 1,
                      padding: "0.75rem",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      fontSize: "16px"
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !searchLoading) {
                        handleConversationSearch();
                      }
                    }}
                    disabled={searchLoading}
                  />
                  <button
                    onClick={handleConversationSearch}
                    disabled={!searchQuery.trim() || searchLoading}
                    style={{
                      padding: "0.75rem 1.5rem",
                      background: searchLoading ? "#ccc" : "#2e7d32",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: searchLoading ? "not-allowed" : "pointer",
                      fontSize: "16px"
                    }}
                  >
                    {searchLoading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>
              
              {searchLoading && (
                <div style={{ 
                  textAlign: "center", 
                  padding: "2rem",
                  color: "#666"
                }}>
                  <div style={{ marginBottom: "1rem" }}>Searching your conversations...</div>
                  <div style={{ fontSize: "14px" }}>
                    Looking for "{searchQuery}" in your conversation history
                  </div>
                </div>
              )}
              
              {searchResults && (
                <div style={{ 
                  border: "1px solid #ddd", 
                  borderRadius: "8px", 
                  padding: "1rem",
                  background: "#f9f9f9",
                  marginTop: "1rem"
                }}>
                  <div style={{ 
                    fontWeight: "bold", 
                    marginBottom: "1rem",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}>
                    <span>Search Results for "{searchResults.query}" ({searchResults.totalFound} found)</span>
                    <button
                      onClick={clearSearch}
                      style={{
                        background: "#6c757d",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        padding: "0.25rem 0.5rem",
                        fontSize: "12px",
                        cursor: "pointer"
                      }}
                    >
                      Clear
                    </button>
                  </div>
                  
                  {searchResults.results.length === 0 ? (
                    <div style={{ 
                      textAlign: "center", 
                      padding: "2rem",
                      color: "#666"
                    }}>
                      No conversations found containing "{searchResults.query}".
                      <br />
                      <span style={{ fontSize: "14px" }}>
                        Try searching for different keywords or check spelling.
                      </span>
                    </div>
                  ) : (
                    <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                      {searchResults.results.map((result, index) => (
                        <div key={index} style={{
                          background: "white",
                          padding: "1rem",
                          borderRadius: "6px",
                          marginBottom: "0.5rem",
                          border: "1px solid #e0e0e0"
                        }}>
                          <div style={{
                            fontWeight: "bold",
                            marginBottom: "0.5rem",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                          }}>
                            <span style={{ color: "#2e7d32" }}>{result.title}</span>
                            <button
                              onClick={() => {
                                // Load this conversation
                                const conversation = conversations.find(c => 
                                  (c._id || c.id) === result.conversationId
                                );
                                if (conversation) {
                                  setCurrentConversation(conversation);
          setSentDocuments([]);
                                  loadConversation(result.conversationId);
                                  setShowSearchModal(false);
                                }
                              }}
                              style={{
                                background: "#2e7d32",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                padding: "0.25rem 0.5rem",
                                fontSize: "12px",
                                cursor: "pointer"
                              }}
                            >
                              Open
                            </button>
                          </div>
                          <div style={{ fontSize: "12px", color: "#666", marginBottom: "0.5rem" }}>
                            {result.matchCount} matches • Last activity: {new Date(result.lastActivity).toLocaleDateString()}
                          </div>
                          {result.relevantMessages && result.relevantMessages.length > 0 && (
                            <div>
                              {result.relevantMessages.map((message, msgIndex) => (
                                <div key={msgIndex} style={{
                                  background: "#f8f9fa",
                                  padding: "0.5rem",
                                  borderRadius: "4px",
                                  marginBottom: "0.25rem",
                                  fontSize: "13px",
                                  borderLeft: message.type === 'user' ? "3px solid #007bff" : "3px solid #2e7d32"
                                }}>
                                  <div style={{ fontWeight: "bold", fontSize: "11px", marginBottom: "0.25rem" }}>
                                    {message.type === 'user' ? 'You' : 'Gene'} • {new Date(message.timestamp).toLocaleString()}
                                  </div>
                                  <div dangerouslySetInnerHTML={{ 
                                    __html: (message.content || message.preview || '').replace(
                                      new RegExp(searchResults.query, 'gi'), 
                                      `<mark style="background: yellow;">$&</mark>`
                                    )
                                  }} />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div style={{ 
                    fontSize: "12px", 
                    color: "#666", 
                    marginTop: "0.5rem",
                    textAlign: "center"
                  }}>
                    Search completed at {searchResults.timestamp.toLocaleTimeString()}
                  </div>
                </div>
              )}
              
              {!searchLoading && !searchResults && (
                <div style={{ 
                  textAlign: "center", 
                  padding: "2rem",
                  color: "#666"
                }}>
                  <div style={{ fontSize: "48px", marginBottom: "1rem" }}>Search</div>
                  <div style={{ marginBottom: "0.5rem" }}>Search Your Genealogy Research</div>
                  <div style={{ fontSize: "14px" }}>
                    Find information about family names, research findings, document analysis, and genealogy insights from your conversation history.
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Document Popup Modal */}
        {showDocumentPopup && (
          <div
            className="document-popup-overlay"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              zIndex: 9999000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "2rem"
            }}
            onClick={() => setShowDocumentPopup(false)}
          >
            <div
              className="document-popup"
              style={{
                background: "white",
                borderRadius: "12px",
                padding: "2rem",
                maxWidth: "500px",
                maxHeight: "70vh",
                overflow: "auto",
                boxShadow: "0 12px 36px rgba(0,0,0,0.3)"
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                <h3 style={{ margin: 0 }}>Select Documents</h3>
                <button
                  onClick={() => setShowDocumentPopup(false)}
                  style={{
                    background: "#dc3545",
                    color: "white",
                    border: "none",
                    borderRadius: "50%",
                    width: "30px",
                    height: "30px",
                    cursor: "pointer"
                  }}
                >
                  ✕
                </button>
              </div>
              <div style={{ marginBottom: "1rem", fontSize: "0.9rem", color: "#666" }}>
                Select documents for Gene to analyze:
              </div>
              {availableDocuments.map(doc => (
                <label
                  key={doc._id}
                  style={{
                    display: "block",
                    padding: "0.75rem",
                    marginBottom: "0.5rem",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    cursor: "pointer",
                    background: selectedDocuments.includes(doc._id) ? "#e8f5e9" : "white"
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedDocuments.includes(doc._id)}
                    onChange={() => toggleDocumentSelection(doc._id)}
                    style={{ marginRight: "0.5rem" }}
                  />
                  {doc.originalName} ({doc.documentType})
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatInterface;
